import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { navigate } from '../lib/router.ts';
import { ensureProtocol, hostOf } from '../lib/utils.ts';
import { EditableText } from '../components/EditableText.tsx';
import type { LandingRowDTO, ServiceRequestDTO, SessionUserDTO } from '@newflix/shared';
import { LOGO_URL } from '../lib/assets.ts';

const PROFILE_ERR_MSG: Record<string, string> = {
  username_taken: '이미 사용 중인 사용자명입니다.',
  unsupported_type: 'PNG/JPG/WEBP/GIF 이미지만 업로드할 수 있습니다.',
  file_too_large: '이미지 용량은 2MB 이하여야 합니다.',
  storage_unavailable: '이미지 저장소가 설정되지 않았습니다.',
};

function profileErr(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return PROFILE_ERR_MSG[err.message] ?? fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

const DELETE_CONFIRM_PHRASE = '계정을 삭제합니다.';

interface MePageProps {
  favs: string[];
}

interface Stats {
  saves: number;
  reviews: number;
  collections: number;
}

export function MePage({ favs }: MePageProps) {
  const [user, setUser] = useState<SessionUserDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteVerified, setDeleteVerified] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.me();
        setUser(meRes.user);
        try {
          const s = await fetch('/api/users/me/stats', { credentials: 'include' });
          if (s.ok) setStats((await s.json()) as Stats);
        } catch {
          /* ignore */
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSaveProfile = async (patch: { username?: string; displayName?: string }) => {
    try {
      const updated = await api.updateProfile(patch);
      setUser((prev) => (prev ? { ...prev, ...updated } : updated));
      setFeedback({ kind: 'ok', msg: '프로필을 저장했습니다.' });
    } catch (err) {
      throw new Error(profileErr(err, '프로필 저장에 실패했습니다.'));
    }
  };

  const onPickAvatar = () => fileInputRef.current?.click();

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    setUploadingAvatar(true);
    setFeedback(null);
    try {
      const { avatar } = await api.uploadAvatar(file);
      setUser((prev) => (prev ? { ...prev, avatar } : prev));
      setFeedback({ kind: 'ok', msg: '프로필 사진을 변경했습니다.' });
    } catch (err) {
      setFeedback({ kind: 'err', msg: profileErr(err, '사진 업로드에 실패했습니다.') });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    setFeedback(null);
    try {
      await api.exportMyData();
      setFeedback({ kind: 'ok', msg: '데이터를 다운로드했습니다.' });
    } catch (err) {
      setFeedback({
        kind: 'err',
        msg: err instanceof Error ? err.message : '내보내기에 실패했습니다.',
      });
    } finally {
      setExporting(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    setFeedback(null);
    try {
      const res = await api.deleteAccount();
      setFeedback({
        kind: 'ok',
        msg: `${res.message} (영구 삭제 예정: ${new Date(res.purgeAt).toLocaleString('ko-KR')})`,
      });
      setTimeout(() => {
        window.location.href = '/';
      }, 2500);
    } catch (err) {
      setFeedback({
        kind: 'err',
        msg: err instanceof Error ? err.message : '계정 삭제에 실패했습니다.',
      });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const onLogout = async () => {
    try {
      await api.logout();
    } finally {
      window.location.href = '/';
    }
  };

  if (loading) {
    return (
      <main className="nf-page" style={{ paddingTop: 120, textAlign: 'center', color: 'var(--fg-muted)' }}>
        불러오는 중…
      </main>
    );
  }

  // 비로그인 → 로그인 유도 화면
  if (!user) {
    return <LoginInvite />;
  }

  return (
    <main className="nf-page" style={{ paddingTop: 120 }}>
      <div
        style={{
          padding: '0 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 32,
          maxWidth: 560,
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            textAlign: 'left',
          }}
        >
          <button
            type="button"
            onClick={onPickAvatar}
            disabled={uploadingAvatar}
            title="클릭하여 프로필 사진 변경"
            style={{
              position: 'relative',
              flexShrink: 0,
              width: 96,
              height: 96,
              borderRadius: '50%',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              background: user.avatar
                ? `url(${user.avatar}) center/cover`
                : 'linear-gradient(135deg, #7C5CFF 0%, #FF6B9D 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              fontWeight: 900,
              color: 'white',
            }}
          >
            {user.avatar ? '' : (user.displayName ?? user.username)[0]}
            <span
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: 'white',
                background: 'rgba(0,0,0,.5)',
                opacity: uploadingAvatar ? 1 : 0,
                transition: 'opacity .15s',
              }}
              className="nf-avatar-overlay"
            >
              {uploadingAvatar ? '업로드 중…' : '사진 변경'}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={onAvatarChange}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <EditableText
              value={user.displayName}
              maxLength={80}
              onSave={(next) => onSaveProfile({ displayName: next.trim() })}
            >
              <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: '-.02em', margin: '0 0 6px' }}>
                {user.displayName}
              </h2>
            </EditableText>
            <div style={{ color: 'var(--fg-muted)', fontSize: 14, marginBottom: 14 }}>
              @
              <EditableText
                value={user.username}
                maxLength={32}
                onSave={(next) => onSaveProfile({ username: next.trim().toLowerCase() })}
                style={{ display: 'inline' }}
              />
              {` · ${user.email}`}
            </div>
          </div>
        </div>
        <div style={{ width: '100%', textAlign: 'left' }}>
          <h3 className="nf-section-title">활동 통계</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 28 }}>
            <Stat label="찜한 서비스" value={stats?.saves ?? favs.length} />
            <Stat label="작성한 리뷰" value={stats?.reviews ?? 0} />
            <Stat label="컬렉션" value={stats?.collections ?? 0} />
          </div>

          {/* 내가 요청한 서비스 */}
          <MyRequestsSection />

          {/* 내 랜딩 큐레이션 순서 */}
          <MyLandingRows />

          {/* 로그아웃 섹션 */}
          <section style={{ marginTop: 28 }}>
            <h3 className="nf-section-title">로그아웃</h3>
            <button className="nf-btn" onClick={onLogout}>
              로그아웃
            </button>
          </section>

          <hr className="nf-me-divider" />

          {/* 고급 — 내 데이터 / 계정 삭제 */}
          <details className="nf-fold">
            <summary>
              <h3 className="nf-section-title">고급</h3>
            </summary>
            <div className="nf-fold-body">
              <details className="nf-fold nf-fold-sub">
                <summary>
                  <h4 className="nf-subsection-title">내 데이터</h4>
                </summary>
                <div className="nf-fold-body">
                  <p className="nf-me-help">
                    회원님의 프로필, 찜, 리뷰, 컬렉션 데이터를 JSON 파일로 내려받을 수 있습니다 (
                    <a href="/legal/privacy">개인정보처리방침</a>).
                  </p>
                  <button className="nf-btn" disabled={exporting} onClick={onExport}>
                    {exporting ? '내보내는 중…' : '내 데이터 내보내기 (JSON)'}
                  </button>
                </div>
              </details>

              <details className="nf-fold nf-fold-sub">
                <summary>
                  <h4 className="nf-subsection-title">계정 삭제</h4>
                </summary>
                <div className="nf-fold-body">
                  <p className="nf-me-help">
                    계정을 삭제하면 즉시 비활성화되고, 30일 후 영구 삭제됩니다. 30일 내 동일한 Google 계정으로
                    로그인하면 자동 복구됩니다.
                  </p>
                  {!deleteVerified ? (
                    <button
                      className="nf-btn"
                      onClick={() => {
                        setConfirmText('');
                        setConfirmDelete(true);
                      }}
                    >
                      계정 삭제 진행
                    </button>
                  ) : (
                    <button
                      className="nf-btn danger"
                      disabled={deleting}
                      onClick={() => {
                        if (window.confirm('계정을 영구 삭제 절차를 시작합니다. 진행할까요?')) onDelete();
                      }}
                    >
                      {deleting ? '삭제 중…' : '계정 삭제'}
                    </button>
                  )}
                </div>
              </details>
            </div>
          </details>

          {feedback && (
            <div className={`nf-me-feedback ${feedback.kind}`} role="status">
              {feedback.msg}
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div
          className="nf-modal-overlay"
          onClick={() => {
            if (!deleting) setConfirmDelete(false);
          }}
        >
          <div className="nf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 10px' }}>계정 삭제</h3>
            <p style={{ fontSize: 14, color: 'var(--fg-soft)', lineHeight: 1.6, margin: '0 0 18px' }}>
              <strong>{user.displayName}</strong> ({user.email}) 계정을 삭제합니다. 즉시 비활성화되고 30일 후
              영구 삭제됩니다. 계속하려면 아래에{' '}
              <strong style={{ color: 'var(--fg)' }}>{DELETE_CONFIRM_PHRASE}</strong> 를 입력하세요.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={DELETE_CONFIRM_PHRASE}
              disabled={deleting}
              autoFocus
              className="nf-modal-input"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button
                className="nf-btn"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
              >
                취소
              </button>
              <button
                className="nf-btn danger"
                disabled={confirmText.trim() !== DELETE_CONFIRM_PHRASE}
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteVerified(true);
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.344 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.9641 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}

function LoginInvite() {
  const perks = [
    { icon: '❤️', t: '찜 동기화', d: '기기 바꿔도 찜한 서비스 그대로' },
    { icon: '📚', t: '컬렉션', d: '나만의 서비스 모음 만들고 공유' },
    { icon: '✍️', t: '리뷰', d: '써본 서비스에 평점·후기 남기기' },
  ];
  return (
    <main className="nf-page" style={{ paddingTop: 120 }}>
      <div
        style={{
          maxWidth: 460,
          margin: '0 auto',
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        <div
          className="nf-logo"
          style={{ justifyContent: 'center', fontSize: 52, gap: 14, margin: '0 0 40px' }}
        >
          <img src={LOGO_URL} className="nf-logo-img" alt="" style={{ height: 50 }} />
          <span>
            <span className="n">n</span>ewflix
          </span>
        </div>

        <a href="/api/auth/google" className="nf-google-btn">
          <GoogleIcon />
          <span>Google 계정으로 로그인</span>
        </a>

        <div
          style={{
            display: 'grid',
            gap: 12,
            marginTop: 36,
            textAlign: 'left',
          }}
        >
          {perks.map((p) => (
            <div
              key={p.t}
              style={{
                display: 'flex',
                gap: 14,
                alignItems: 'center',
                padding: '14px 16px',
                background: 'var(--bg-card)',
                border: '1px solid var(--line-soft)',
                borderRadius: 12,
              }}
            >
              <span style={{ fontSize: 22 }}>{p.icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.t}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2 }}>{p.d}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 28 }}>
          로그인 시{' '}
          <a href="/legal/tos" style={{ color: 'var(--fg-muted)' }}>
            이용약관
          </a>{' '}
          ·{' '}
          <a href="/legal/privacy" style={{ color: 'var(--fg-muted)' }}>
            개인정보처리방침
          </a>
          에 동의하게 됩니다.
        </p>
      </div>
    </main>
  );
}

function MyLandingRows() {
  const queryClient = useQueryClient();
  const rowsQ = useQuery({ queryKey: qk.myLandingRows(), queryFn: () => api.myLandingRows() });
  const [order, setOrder] = useState<LandingRowDTO[]>([]);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (rowsQ.data) {
      setOrder(rowsQ.data.rows);
      setDirty(false);
    }
  }, [rowsQ.data]);

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setOrder(next);
    setDirty(true);
  };

  const saveMut = useMutation({
    mutationFn: () => api.reorderLandingRows(order.map((r) => r.rowId)),
    onSuccess: async () => {
      setDirty(false);
      setFeedback('순서를 저장했습니다.');
      await queryClient.invalidateQueries({ queryKey: qk.myLandingRows() });
      await queryClient.invalidateQueries({ queryKey: qk.landing() });
    },
    onError: () => setFeedback('저장에 실패했습니다.'),
  });

  const SOURCE_LABEL: Record<string, string> = {
    system: '시스템',
    admin_default: '기본',
    user: '내 추가',
  };

  return (
    <section style={{ marginTop: 28, textAlign: 'left' }}>
      <h3 className="nf-section-title">내 랜딩 큐레이션 순서</h3>
      <p className="nf-me-help">
        랜딩 페이지에 표시되는 큐레이션 순서를 조정합니다. 컬렉션 추가/제거는{' '}
        <a href="/collections">컬렉션 둘러보기</a>에서 관리합니다.
      </p>
      {rowsQ.isPending ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>불러오는 중…</div>
      ) : order.length === 0 ? (
        <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>표시할 큐레이션이 없어요.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {order.map((row, i) => (
            <div
              key={row.rowId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: 'var(--bg-card)',
                border: '1px solid var(--line-soft)',
                borderRadius: 10,
              }}
            >
              <span style={{ fontSize: 13, color: 'var(--fg-dim)', width: 20 }}>{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {row.emoji ? `${row.emoji} ` : ''}
                  {row.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                  {SOURCE_LABEL[row.source] ?? row.source} · 서비스 {row.services.length}개
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  type="button"
                  className="nf-btn"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  aria-label="위로"
                  style={{ padding: '4px 10px' }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="nf-btn"
                  disabled={i === order.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label="아래로"
                  style={{ padding: '4px 10px' }}
                >
                  ↓
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(dirty || feedback) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button
            type="button"
            className="nf-btn primary"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? '저장 중…' : '순서 저장'}
          </button>
          {feedback && !dirty && (
            <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{feedback}</span>
          )}
        </div>
      )}
    </section>
  );
}

// 요청 상태 → 사용자용 라벨/색. hidden 은 유저에게 '보류 중' 으로 부드럽게.
function requestStatusBadge(r: ServiceRequestDTO): { label: string; bg: string; fg: string } {
  if (r.status === 'rejected') return { label: '거절됨', bg: 'rgba(248,113,113,.15)', fg: '#f87171' };
  if (r.status === 'promoted') {
    return r.linkedSlug
      ? { label: '등록됨', bg: 'var(--positive)', fg: '#06240f' }
      : { label: '등록 진행 중', bg: 'rgba(124,92,255,.18)', fg: '#9a8cff' };
  }
  if (r.status === 'hidden') return { label: '보류 중', bg: 'rgba(255,255,255,.06)', fg: 'var(--fg-muted)' };
  return { label: '검토 중', bg: 'rgba(255,255,255,.06)', fg: 'var(--fg-soft)' };
}


function MyRequestsSection() {
  const [open, setOpen] = useState(false);
  // 모달 열릴 때만 fetch (enabled).
  const q = useQuery({
    queryKey: qk.requests.mine(),
    queryFn: () => api.myRequests(1, 50),
    enabled: open,
  });
  const items = q.data?.items ?? [];

  return (
    <section style={{ marginTop: 28, textAlign: 'left' }}>
      <h3 className="nf-section-title">내가 요청한 서비스</h3>
      <p className="nf-me-help">
        제안한 서비스의 검토 상태를 확인합니다. 새 서비스 제안은{' '}
        <a href="/requests">서비스 요청</a>에서 할 수 있어요.
      </p>
      <button type="button" className="nf-btn" onClick={() => setOpen(true)}>
        내 요청 보기
      </button>

      {open && (
        <div className="nf-modal-overlay" onClick={() => setOpen(false)}>
          <div
            className="nf-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 20, fontWeight: 900, margin: 0 }}>내가 요청한 서비스</h3>
              <button
                type="button"
                className="nf-btn ghost"
                style={{ height: 30, padding: '0 10px', fontSize: 12 }}
                onClick={() => setOpen(false)}
              >
                닫기
              </button>
            </div>

            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.isPending ? (
                <div style={{ color: 'var(--fg-muted)', fontSize: 13, padding: '20px 0' }}>불러오는 중…</div>
              ) : items.length === 0 ? (
                <div style={{ color: 'var(--fg-muted)', fontSize: 14, padding: '32px 0', textAlign: 'center' }}>
                  아직 요청한 서비스가 없어요.
                </div>
              ) : (
                items.map((r) => {
                  const badge = requestStatusBadge(r);
                  return (
                    <div
                      key={r.id}
                      style={{
                        padding: '12px 14px',
                        border: '1px solid var(--line-soft)',
                        borderRadius: 10,
                        background: 'var(--bg-card)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0 }}>{r.name}</span>
                        {r.linkedSlug ? (
                          <a
                            href={'/s/' + r.linkedSlug}
                            onClick={(e) => {
                              e.preventDefault();
                              setOpen(false);
                              navigate('/s/' + r.linkedSlug);
                            }}
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 5,
                              background: badge.bg,
                              color: badge.fg,
                              fontWeight: 700,
                            }}
                          >
                            {badge.label} →
                          </a>
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 5,
                              background: badge.bg,
                              color: badge.fg,
                              fontWeight: 700,
                            }}
                          >
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <a
                        href={ensureProtocol(r.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12, color: 'var(--fg-dim)' }}
                      >
                        {hostOf(r.url)}
                      </a>
                      <span style={{ fontSize: 12, color: 'var(--fg-dim)', marginLeft: 8 }}>
                        ▲ {r.likeCount} · 💬 {r.commentCount}
                      </span>
                      {r.status === 'rejected' && r.rejectionReason && (
                        <div
                          style={{
                            marginTop: 8,
                            padding: '8px 10px',
                            borderRadius: 8,
                            background: 'rgba(248,113,113,.08)',
                            border: '1px solid rgba(248,113,113,.2)',
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: 'var(--fg-soft)',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <strong style={{ color: '#f87171', fontWeight: 700 }}>사유 </strong>
                          {r.rejectionReason}
                        </div>
                      )}
                      {r.commentCount > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <a
                            href="/requests"
                            onClick={(e) => {
                              e.preventDefault();
                              setOpen(false);
                              navigate('/requests');
                            }}
                            style={{ fontSize: 12, color: '#9a8cff', fontWeight: 600 }}
                          >
                            대화 보기 →
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="nf-aside-card" style={{ margin: 0 }}>
      <h4>{label}</h4>
      <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: '-.02em' }}>{value}</div>
    </div>
  );
}
